import { z } from 'zod'

/**
 * Content-bounds measurement + trim decision, shared by the server composer
 * and the client trial preview so the two renderings cannot drift.
 *
 * Measurement happens in the Kotlin pdf-processor (ContentBoundsAnalyzer.kt):
 * each page is rendered to pixels and row-scanned for the first/last content
 * row, yielding per-page top/bottom margins. This module turns that report
 * into the trim actually applied at composition — and is deliberately the
 * ONLY place trim policy lives.
 *
 * The trim is applied silently, so every rule here fails toward "no trim".
 */

/** Must match CONTENT_BOUNDS_VERSION in ContentBoundsAnalyzer.kt. */
export const CONTENT_BOUNDS_VERSION = 1

export const pageContentBoundsSchema = z.object({
  page: z.number().int().min(0),
  pageWidthPt: z.number().positive(),
  pageHeightPt: z.number().positive(),
  topMarginPt: z.number().min(0),
  bottomMarginPt: z.number().min(0),
  reliable: z.boolean(),
  reason: z.string().optional(),
})

export const contentBoundsReportSchema = z.object({
  version: z.number().int(),
  pages: z.array(pageContentBoundsSchema),
})

export type PageContentBounds = z.infer<typeof pageContentBoundsSchema>
export type ContentBoundsReport = z.infer<typeof contentBoundsReportSchema>

export const CONTENT_TRIM = {
  /** Source-page margin always retained on a trimmed edge (0.2in). */
  RESIDUAL_GAP_PT: 14.4,
  /** Never remove more than 1in per edge. */
  MAX_TRIM_PT: 72,
  /** Reported and embedded page sizes must agree within this tolerance. */
  PAGE_SIZE_TOLERANCE_PT: 1,
} as const

export interface PageTrim {
  topPt: number
  bottomPt: number
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(Math.max(v, lo), hi)

/**
 * Decides the top/bottom trim applied to EVERY composed resume page.
 *
 * Measured per page but applied uniformly (the min margin across pages):
 * per-page trims would change each page's fit scale, making body text
 * visibly resize between pages, and the min is also the conservative
 * envelope — no page can be cropped past its own measured margin.
 *
 * Returns null (no trim) when anything doesn't add up: missing/stale report,
 * fewer measured pages than composed pages, any page the analyzer couldn't
 * vouch for, or a reported page size that disagrees with what the composer
 * is actually embedding (belt-and-braces against CropBox/rotation surprises).
 */
export function computeUniformTrim(
  bounds: ContentBoundsReport | null | undefined,
  composedPageCount: number,
  srcPageSizesPt: Array<{ width: number; height: number }>
): PageTrim | null {
  if (!bounds || bounds.version !== CONTENT_BOUNDS_VERSION) return null
  if (composedPageCount <= 0) return null
  const pages = bounds.pages.slice(0, composedPageCount)
  if (pages.length < composedPageCount) return null
  if (pages.some(p => !p.reliable)) return null
  for (let i = 0; i < pages.length; i++) {
    const src = srcPageSizesPt[i]
    if (
      !src ||
      Math.abs(pages[i].pageWidthPt - src.width) >
        CONTENT_TRIM.PAGE_SIZE_TOLERANCE_PT ||
      Math.abs(pages[i].pageHeightPt - src.height) >
        CONTENT_TRIM.PAGE_SIZE_TOLERANCE_PT
    ) {
      return null
    }
  }

  const top = Math.min(...pages.map(p => p.topMarginPt))
  const bottom = Math.min(...pages.map(p => p.bottomMarginPt))
  const trim: PageTrim = {
    topPt: clamp(
      top - CONTENT_TRIM.RESIDUAL_GAP_PT,
      0,
      CONTENT_TRIM.MAX_TRIM_PT
    ),
    bottomPt: clamp(
      bottom - CONTENT_TRIM.RESIDUAL_GAP_PT,
      0,
      CONTENT_TRIM.MAX_TRIM_PT
    ),
  }
  if (trim.topPt === 0 && trim.bottomPt === 0) return null
  return trim
}

/**
 * Parses an untrusted content-bounds payload (Lambda response field, S3
 * sidecar object, API response). Returns null on any mismatch — never throws.
 */
export function parseContentBoundsReport(
  value: unknown
): ContentBoundsReport | null {
  const parsed = contentBoundsReportSchema.safeParse(value)
  if (!parsed.success) return null
  if (parsed.data.version !== CONTENT_BOUNDS_VERSION) return null
  return parsed.data
}
